import React, { useCallback } from 'react';
import { Box, useIsDesktopView } from 'deepstash-ui';
import { Source } from 'types/models';
import InfiniteList from 'src/components/list/infinite-list/InfiniteList';
import NextLink from 'src/components/navigation/NextLink';
import { slugify, SQUARE_WIDTH_REM } from 'utils/global';
import SourceCollapsed from 'src/components/source/source-collapsed/SourceCollapsed';
import { Analytics, Events } from 'src/services/analytics';
import SourceCollapsedPlaceholder from 'src/components/source/source-collapsed-placeholder/SourceCollapsedPlaceholder';
import commonStrings from 'utils/strings/commonStrings';
import SubsectionHeader from 'src/components/metacontent/components/SubsectionHeader';
import useAuth from 'providers/hooks/useAuth';
import SignUpBanner from 'src/components/banner/SignUpBanner';

interface TopicContentProps {
  sources: Source[];
  hashtag: string;
  hasNextPage?: boolean;
}

const TopicContent: React.FC<TopicContentProps> = ({ sources }) => {
  const { isLoggedIn } = useAuth();
  const isDesktopView = useIsDesktopView();

  const getHref = useCallback(
    source => `/article/${source.id}/${slugify(source.title)}`,
    [],
  );

  const onPreviewClick = (source: Source) => {
    Analytics.logEvent({
      eventName: Events.consumption.sourcePreviewClick,
      properties: {
        sourceId: source.id,
        element: 'preview-idea',
        location: 'hashtag-screen',
      },
      platforms: ['amplitude'],
    });
  };

  const renderItem = (source: Source, index: number) => {
    return (
      <Box
        onClick={() => onPreviewClick(source)}
        key={`topic-source-${source.id}-${index}`}
      >
        <NextLink href={getHref(source)}>
          <SourceCollapsed
            key={source.id}
            source={source}
            mb={SQUARE_WIDTH_REM}
          />
        </NextLink>
      </Box>
    );
  };

  return (
    <Box pos="relative" zIndex={3}>
      <SubsectionHeader>
        {commonStrings.allIdeas.toUpperCase()}
      </SubsectionHeader>
      {/* first of all render 2 items, then SignUpBanner if NSI and on Tablet or Moblie */}
      {sources.length > 0 && renderItem(sources[0], 0)}
      {sources.length > 1 && renderItem(sources[1], 1)}
      {!isLoggedIn && !isDesktopView && <SignUpBanner mb={SQUARE_WIDTH_REM} />}
      <InfiniteList
        listKey="topic"
        data={sources.slice(2)}
        paddingThreshold={5}
        initialNumToRender={5}
        newItemsThreshold={5}
        identifier={source => source.id.toString()}
        renderItem={renderItem}
        renderPlaceholder={(w, h) => (
          <SourceCollapsedPlaceholder width={w ?? 'unset'} h={h ?? 'unset'} />
        )}
      />
    </Box>
  );
};

export default TopicContent;
